import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'

import { ArrowDown4 } from '@aok/icon-functional'
import { BarGraph, Icon, Tooltip } from 'components/atoms'

import * as Styled from './Accordion.styles'

import { useCollapse } from 'hooks'
import { getHeaderHeight } from 'settings/app/uiHelpers'

/**
 * functional container for all detail views
 * @returns {*}
 * @constructor
 */
const Accordion = React.memo((props) => {
  const {
    label = '',
    tooltip = null,
    ratingValue = null,
    children = null,
    open = false,
    ratingAverage = null,
    showRatingAverage = false,
    type = 'DEFAULT',
    className = '',
    triggerComponentInstance = null,
    disclaimerAnchor = null,
    triggerStateChangeToParent = null,
  } = props

  const [isOpen, setIsOpen] = useState(open)
  const timer = React.useRef(null)

  const collapse = useCollapse(open)

  // overwrite internal state
  useEffect(() => {
    setIsOpen(open)
    collapse.toggle(open)
    // eslint-disable-next-line
  }, [open])

  /**
   * clean up on component destroy
   */
  useEffect(
    () => () => {
      if (timer.current) {
        clearTimeout(timer.current)
      }
    },
    [timer]
  )

  const getModifierByType = () => {
    switch (type) {
      case 'RATING':
        return 'accordion-rating'
      case 'UNDERLINED':
        return 'accordion-underlined'
      case 'ASV':
        return 'accordion-asv'
      case 'CUSTOM-TRIGGER':
        return 'accordion-custom-trigger'
      default:
        return ''
    }
  }

  const handleToggle = () => {
    if (triggerStateChangeToParent) {
      triggerStateChangeToParent()
    } else {
      setIsOpen((prevState) => !prevState)
      collapse.toggle()
    }
  }

  return (
    <Styled.Accordion
      className={ `${ getModifierByType() } ${ className }` }
      ratingAverage={ ratingAverage ? ratingAverage : 0 }>
      <Styled.ToggleTrigger
        style-open={ isOpen }
        { ...collapse.triggerProps }
        onClick={ handleToggle }>
        {label && (
          <Styled.TriggerLabel
            tag={ type === 'DEFAULT' ? 'h4' : 'div' }
            className={ type === 'DEFAULT' ? 'h4' : '' }>
            {label}
            {ratingAverage && showRatingAverage && (
              <span className='average-value'> (&empty; {ratingAverage}%)</span>
            )}
            {disclaimerAnchor ? (
              <a
                className={ 'disclaimer-anchor' }
                href={ `#${ disclaimerAnchor }` }
                onClick={ (e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  const element = document.getElementById(disclaimerAnchor)
                  const y =
                    element?.getBoundingClientRect().top +
                    window.scrollY -
                    getHeaderHeight()
                  window.scrollTo({ top: y, behavior: 'smooth' })
                } }>
                &nbsp;*
              </a>
            ) : (
              ''
            )}
            {tooltip && (
              <Styled.InfoIconContainer>
                <Tooltip
                  infoIconId={ 'info-icon' }
                  tooltipId={ 'accordion-header-tooltip' }
                  dialogTitle={ label }>
                  {tooltip}
                </Tooltip>
              </Styled.InfoIconContainer>
            )}
            {type === 'UNDERLINED' && (
              <Icon className={ 'accordion-icon' } icon={ <ArrowDown4 /> } />
            )}
          </Styled.TriggerLabel>
        )}
        {ratingValue && (
          <span className={ 'accordion-bar-graph' }>
            <BarGraph
              className={ 'accordion-bar-graph' }
              percent={ ratingValue }
              average={ ratingAverage }
              showAverage={ showRatingAverage }
            />
          </span>
        )}
        {/* optional component instance will be rendered within the trigger component */}
        {triggerComponentInstance && triggerComponentInstance}
        {type !== 'UNDERLINED' && (
          <Icon className={ 'accordion-icon' } icon={ <ArrowDown4 /> } />
        )}
      </Styled.ToggleTrigger>

      <Styled.ContentContainer { ...collapse.containerProps }>
        <Styled.ContentWrapper>
          <Styled.Content>{children}</Styled.Content>
        </Styled.ContentWrapper>
      </Styled.ContentContainer>
    </Styled.Accordion>
  )
})

Accordion.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  tooltip: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  ratingValue: PropTypes.number,
  ratingAverage: PropTypes.number,
  showRatingAverage: PropTypes.bool,
  open: PropTypes.bool,
  type: PropTypes.oneOf([
    'DEFAULT',
    'RATING',
    'UNDERLINED',
    'ASV',
    'CUSTOM-TRIGGER',
  ]),
  triggerComponentInstance: PropTypes.any,
  // disclaimerAnchor: PropTypes.string,
  triggerStateChangeToParent: PropTypes.func,
}

export default Accordion
